import styled from 'styled-components';

import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconApi } from '@components/icons/api';
import { IconForm } from '@components/icons/form';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconMailhook } from '@components/icons/mailhook';
import { IconTrigger } from '@components/icons/trigger';
import { IconWebhook } from '@components/icons/webhook';
import { InfoBox } from '@components/info-box';
import { ListCard } from '@components/list-card';
import { forceHideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

import { useCreateEmailTrigger } from 'api/email-triggers/hooks';
import { useCreateJourneyWebhook } from 'api/journey-webhooks/hooks';

import { useEmailTriggerSidebar } from './email-trigger-sidebar';
import { useScheduledPollingSidebar } from './scheduled-polling';
import { useWebhookSidebar } from './webhook-sidebar';

import { clearFormParam, setFormParam } from '../form-url-params';
import { useEditJourneyFormOverlay } from '../journey-form/edit-journey-form-overlay';
import { useCreateDefaultJourneyForm } from '../journey-form/use-create-default-journey-form';

export const useAddTravellersSidebar = () => {
  const sidebar = useSidebar(AddTravellersSidebar, {
    variant: 'REGULAR',
  });
  return sidebar;
};

type AddTravellersSidebarProps = {
  journeyShortId: string;
};

export const AddTravellersSidebar = ({ journeyShortId }: AddTravellersSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const { createDefault } = useCreateDefaultJourneyForm({ journeyShortId });
  const showWebhookSidebar = useWebhookSidebar();
  const showEmailTriggerSidebar = useEmailTriggerSidebar();
  const showScheduledPollingSidebar = useScheduledPollingSidebar();
  const showEditJourneyFormOverlay = useEditJourneyFormOverlay();

  const createWebhookMutation = useCreateJourneyWebhook(journeyShortId);
  const createEmailTriggerMutation = useCreateEmailTrigger(journeyShortId);

  const handleCreateWebhook = () => {
    createWebhookMutation.mutate(undefined, {
      onSuccess: (data) => {
        showWebhookSidebar({ webhookShortId: data.shortId, journeyShortId });
      },
    });
  };

  const handleCreateEmailTrigger = () => {
    createEmailTriggerMutation.mutate(undefined, {
      onSuccess: (data) => {
        showEmailTriggerSidebar({ triggerShortId: data.shortId, journeyShortId });
      },
    });
  };

  const handleCreateJourneyForm = async () => {
    const formShortId = await createDefault();
    if (formShortId) {
      setFormParam(formShortId);
      showEditJourneyFormOverlay(
        {
          journeyId: journeyShortId,
          formId: formShortId,
        },
        { onHide: clearFormParam }
      );
    }
    forceHideSidebar();
  };

  const advancedCards = [
    {
      Icon: (
        <IconFancyContainer
          variant="orange"
          hasBorder
          icon={<IconWebhook color="elementOrange100" />}
        />
      ),
      title: t('webhook'),
      description: t('webhookDescription'),
      onClick: handleCreateWebhook,
    },
    {
      Icon: (
        <IconFancyContainer
          variant="orange"
          hasBorder
          icon={<IconMailhook color="elementOrange100" />}
        />
      ),
      title: t('emailTrigger'),
      description: t('emailTriggerDescription'),
      onClick: handleCreateEmailTrigger,
    },
    {
      Icon: (
        <IconFancyContainer
          variant="indigo"
          hasBorder
          icon={<IconApi color="elementIndigo100" />}
        />
      ),
      title: t('scheduledPolling'),
      description: t('scheduledPollingDescription'),
      onClick: () => showScheduledPollingSidebar({ journeyShortId }),
    },
  ];

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={IconTrigger}
        iconColor="elementBlue100"
        title={t('addTravellers')}
      />
      <Sidebar.Content>
        <Stack space={4}>
          <Text font="regular14" color="textSecondary" text={t('description')} />
          <HoverableListCard
            icon={
              <IconFancyContainer
                variant="green"
                hasBorder
                icon={<IconForm color="elementGreen200" />}
              />
            }
            title={t('externalForm')}
            description={t('externalFormDescription')}
            onClick={handleCreateJourneyForm}
            disableEllipsis
          />

          <Stack space={2}>
            <Text font="semibold14" text={t('advancedOption')} />
            {advancedCards.map((card, idx) => (
              <HoverableListCard
                key={idx}
                icon={card.Icon}
                title={card.title}
                description={card.description}
                onClick={card.onClick}
                disableEllipsis
              />
            ))}
          </Stack>
          <InfoBox
            text={t('triggerInfo')}
            variant="indigo"
            icon={<IconFormIndicatorInfo color="elementIndigo100" />}
          />
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const HoverableListCard = styled(ListCard)({
  padding: pxToRem(12),
  minHeight: pxToRem(76),
  transition: transitionStandard('background-color'),
  '&:hover': {
    backgroundColor: theme.color.backgroundLightDarkHover,
  },
});

export const locale = {
  en: {
    addTravellers: 'Add journey trigger',
    description:
      'Triggers are how people or systems start your workflow. Choose the methods that work best for your use case',
    externalForm: 'External form',
    externalFormDescription:
      'Create forms that people can fill out to join this Journey\n\n• Display on your company portal\n• Share as a link',
    advancedOption: 'Advanced Options',
    webhook: 'Webhook',
    webhookDescription: 'Create and start travellers through custom HTTP requests',
    emailTrigger: 'Mailhook',
    emailTriggerDescription:
      'Start workflows from inbound emails using AI to extract data from the email body',
    scheduledPolling: 'Scheduled Polling',
    scheduledPollingDescription: 'Scheduled checks at intervals with external data or systems',
    triggerInfo:
      'What happens when your trigger activates?\n\nA new person will be added to this workflow and receive the sequence of messages, tasks, or actions you set up',
  },
};
